import { computed, effect, signal } from '@preact/signals';
import type { FrameOutput } from '@meteoradar/shared/contract/frame.schema';
import type { ProductOutput } from '@meteoradar/shared/contract/product.schema';
import type { ProductCode } from '@meteoradar/shared/enums/product-code';
import { MS_PER_SECOND, SECONDS_PER_MINUTE } from '@meteoradar/shared/time/units';
import { CLOCK_TICK_SECONDS, STALE_AFTER_MINUTES } from '../config.js';
import { DEFAULT_VIEW, type ViewState } from '../render/view-transform.js';
import { readFrameParam, windowHoursFor, withFrameParam } from './deep-link.js';
import { buildUrlSearch, loadPreferences, readUrlOverrides, savePreferences } from './preferences.js';

export const STALE_AFTER_SECONDS = STALE_AFTER_MINUTES * SECONDS_PER_MINUTE;
const CLOCK_TICK_MS = CLOCK_TICK_SECONDS * MS_PER_SECOND;

const initialSearch = globalThis.location?.search ?? '';
const requestedFrame = readFrameParam(initialSearch);
const initial = { ...loadPreferences(), ...readUrlOverrides(initialSearch) };
if (requestedFrame !== null) {
  initial.windowHours = windowHoursFor(requestedFrame, new Date(), initial.windowHours);
}

export const products = signal<ProductOutput[]>([]);
export const product = signal<ProductCode>(initial.product);
export const windowHours = signal<number>(initial.windowHours);
export const speed = signal<number>(initial.speed);
export const frames = signal<FrameOutput[]>([]);
export const cursor = signal(0);
export const playing = signal(requestedFrame === null);
export const pendingFrame = signal<Date | null>(requestedFrame);
export const loadedCount = signal(0);
export const loading = signal(false);
export const error = signal<string | null>(null);
export const now = signal(Date.now());
export const view = signal<ViewState>(DEFAULT_VIEW);
export const exportProgress = signal<{ done: number; total: number } | null>(null);

export const resetView = (): void => {
  view.value = DEFAULT_VIEW;
};

export const currentFrame = computed(() => frames.value[cursor.value] ?? null);
export const latestFrame = computed(() => frames.value.at(-1) ?? null);
export const isAtLatest = computed(() => frames.value.length > 0 && cursor.value === frames.value.length - 1);
export const loadProgress = computed(() => frames.value.length === 0 ? 1 : loadedCount.value / frames.value.length);
export const latestAgeSeconds = computed(() => {
  const latest = latestFrame.value;
  return latest === null ? null : Math.max(0, Math.floor((now.value - Date.parse(latest.ts)) / MS_PER_SECOND));
});
export const isStale = computed(() => latestAgeSeconds.value === null || latestAgeSeconds.value > STALE_AFTER_SECONDS);
export const currentProduct = computed(() => products.value.find(item => item.code === product.value) ?? null);

export const startClock = (): () => void => {
  const timer = setInterval(() => {
    now.value = Date.now();
  }, CLOCK_TICK_MS);
  return () => clearInterval(timer);
};

effect(() => {
  savePreferences({ product: product.value, windowHours: windowHours.value, speed: speed.value });
});

export const shareSearch = computed(() => {
  const base = buildUrlSearch({ product: product.value, windowHours: windowHours.value });
  const frame = currentFrame.value;
  return withFrameParam(base, playing.value || frame === null ? null : frame.ts);
});

effect(() => {
  const search = shareSearch.value;
  if (globalThis.history !== undefined && globalThis.location !== undefined && globalThis.location.search !== search) {
    globalThis.history.replaceState(null, '', `${globalThis.location.pathname}${search}`);
  }
});
